/**
 * "Find My Tools": the front door of the site.
 *
 * Design constraints, in the order they matter:
 *
 *   1. Step one is the three role cards, and they are the quiz rather than an
 *      explainer sitting next to one. They used to be duplicated below the
 *      hero asking the same question a second way.
 *   2. Step two actually narrows. Every option used to return the role it was
 *      reached from, so the second click could not change the outcome.
 *   3. Nothing about the result is revealed before an account exists. The
 *      count, the role and the visitor's own answer are shown, because those
 *      are things they already know or told us; the tool names are what the
 *      free account buys.
 *
 * On the gate specifically: a bare "3 tools match, sign up to see which"
 * hands someone a number in exchange for two answers, which reads as a wall
 * that moved rather than a wall that came down. The panel below instead
 * proves a specific, already-computed answer is waiting: it reads their exact
 * answer back, shows one silhouette per matched tool so the count is visible
 * rather than asserted, and frames the ask as delivery ("your stack is
 * ready") rather than as a toll.
 *
 * The hero height is no longer pinned. It was `sm:min-h-[186px]` so that the
 * tool grid cleared the fold, but that capped step two at a wrapped row of
 * small pills. The documented audience is women 55+ who do not consider
 * themselves technical, and for them a legible, full-width option they can
 * hit on the first try is worth more than the grid being 80px higher.
 */

import { useState } from "react";
import { BUTTON } from "@/lib/buttonStyles";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import type { Role } from "@/lib/toolsData";
import {
  ROLE_CARD_STYLES,
  ROLE_ICONS,
  ROLE_LABELS,
  ROLE_SHORT,
} from "@/lib/roleDisplay";
import {
  QUESTION_ONE,
  QUESTION_TWO,
  STEP_TWO_OPTIONS,
  getRecommendations,
  type QuizIntent,
} from "@/lib/quizData";
import { useAuth } from "@/core/hooks/useAuth";
import { useStack } from "@/hooks/useStack";
import { useAuthModal } from "@/contexts/AuthModalContext";

const ALL_ROLES: Role[] = ["Atlas", "Comet", "Manus"];

interface ProblemFinderProps {
  /** Keeps the grid's role filter in step with the quiz. */
  onRoleSelect: (role: Role) => void;
}

type Step = "role" | "intent" | "result";

export default function ProblemFinder({ onRoleSelect }: ProblemFinderProps) {
  const [step, setStep] = useState<Step>("role");
  const [role, setRole] = useState<Role | null>(null);
  const [intent, setIntent] = useState<QuizIntent | null>(null);

  const { isAuthenticated } = useAuth();

  const { open } = useAuthModal();
  const stack = useStack();

  const reset = () => {
    setStep("role");
    setRole(null);
    setIntent(null);
  };

  const chooseRole = (next: Role) => {
    setRole(next);
    setStep("intent");
  };

  const chooseIntent = (next: QuizIntent) => {
    setIntent(next);
    setStep("result");
    if (role) onRoleSelect(role);
  };

  const scrollToGrid = () =>
    document.getElementById("tools-section")?.scrollIntoView({ behavior: "smooth" });

  // ── STEP 1: the role cards ──────────────────────────────────────────────
  if (step === "role") {
    return (
      <div>
        <StepLabel current={1} />
        <p className="text-base sm:text-lg font-semibold text-foreground mb-3">
          {QUESTION_ONE}
        </p>
        {/* Full-width stacked rows, matching step two and the mockup.
            These were three columns in a grid, which gave each option about a
            third of the width and wrapped "Research, plan, and organize your
            thinking" onto three lines. The first question of the funnel was
            the most cramped thing on the page, and the three cards read as
            labels rather than as something to click. Stacking them gives the
            description one line, room for the chevron that says "this goes
            somewhere", and one target width instead of three. */}
        <div className="flex flex-col gap-2">
          {ALL_ROLES.map((r, i) => (
            <motion.button
              key={r}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1 + i * 0.08 }}
              onClick={() => chooseRole(r)}
              className={`group flex items-center gap-3.5 text-left px-4 min-h-16 rounded-lg border transition-all duration-200 cursor-pointer bg-background/85 border-border/60 hover:border-border ${ROLE_CARD_STYLES[r].hover}`}
            >
              <span
                className={`shrink-0 grid place-items-center w-9 h-9 rounded-full border text-base ${ROLE_CARD_STYLES[r].bg} ${ROLE_CARD_STYLES[r].border}`}
                aria-hidden="true"
              >
                {ROLE_ICONS[r]}
              </span>
              <span className="min-w-0 flex-1">
                <span className="font-['Fraunces'] text-base font-semibold text-foreground block">
                  {ROLE_LABELS[r]}
                </span>
                <span className="text-sm text-foreground/70 leading-snug block">
                  {ROLE_SHORT[r]}
                </span>
              </span>
              <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
            </motion.button>
          ))}
        </div>
      </div>
    );
  }

  // ── STEP 2: narrow it down ──────────────────────────────────────────────
  if (step === "intent" && role) {
    return (
      <div>
        <Trail role={role} onBack={reset} label="Back" />
        <StepLabel current={2} />
        <p className="text-base sm:text-lg font-semibold text-foreground mb-3">
          {QUESTION_TWO}
        </p>
        {/* Full-width rows rather than wrapped pills. Wrapped pills produced
            three different target widths on a phone, and the narrowest were
            under 100px. */}
        <div className="flex flex-col gap-2">
          {STEP_TWO_OPTIONS[role].map(option => (
            <button
              key={option.id}
              onClick={() => chooseIntent(option)}
              className="flex items-center justify-between gap-3 text-left px-4 min-h-14 rounded-lg border border-border bg-background/85 hover:bg-muted hover:border-primary transition-all text-base text-foreground font-medium"
            >
              <span className="leading-snug">{option.label}</span>
              <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── STEP 3: the result, or the gate ─────────────────────────────────────
  if (step === "result" && role && intent) {
    const recommendations = getRecommendations(intent.id);

    return (
      <div>
        <Trail role={role} onBack={reset} label="Start over" intent={intent} />

        {isAuthenticated ? (
          <>
            <p className="text-base sm:text-lg font-semibold text-foreground mb-3">
              {recommendations.length} tools for this job
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 mb-3">
              {recommendations.map(tool => (
                <a
                  key={tool.id}
                  href={tool.routeLink ?? "/"}
                  // Opening a tool from a result is what puts it in the stack.
                  //
                  // Saving all three on sight would fill the stack with things
                  // someone was shown rather than things they chose, and the
                  // point of My Stack is that it reflects decisions. Clicking
                  // in is the decision, and it is also the only moment the
                  // intent is still known, which is what lets the stack say
                  // "matched for: plan a project" later.
                  onClick={() => stack.saveTool(tool.id, intent.id)}
                  className="group flex items-center gap-2 px-4 min-h-12 rounded-lg border border-border bg-background/85 hover:border-primary transition-colors"
                >
                  <span className="text-base font-semibold text-foreground">
                    {tool.emoji} {tool.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {tool.skillLevel}
                  </span>
                  <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-primary" />
                </a>
              ))}
            </div>
          </>
        ) : (
          /* The gate stays compact and the ask happens in the dialog.
             The form was briefly inline here. It read well but it was roughly
             500px tall inside the hero, which pushed the tool grid off the
             bottom of the screen for everyone who took the quiz. The dialog is
             where the ask belongs: it is the only place on the site that can
             be tall without costing anything below it. */
          <div className="rounded-xl border border-border bg-card p-4 sm:p-5 mb-3 shadow-sm">
            <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
              Your result is ready
            </p>
            <p className="font-['Fraunces'] text-lg sm:text-xl font-bold text-foreground mt-1">
              {recommendations.length} tools match this job
            </p>

            {/* One silhouette per match, so the count is something you can see
                rather than a number you are asked to believe. No name, logo or
                initial: the role chip is the only real thing on it, and it is
                what makes the match look earned rather than generic. */}
            <div
              className="grid gap-2 my-4"
              style={{
                gridTemplateColumns: `repeat(${recommendations.length}, minmax(0, 1fr))`,
              }}
              aria-hidden="true"
            >
              {recommendations.map(tool => (
                <div
                  key={tool.id}
                  className="rounded-lg border border-dashed border-border bg-muted/50 p-3"
                >
                  <div className="h-3 w-3/4 rounded bg-foreground/10 mb-2" />
                  <div className="h-2 w-full rounded bg-foreground/5 mb-2" />
                  <span
                    className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${ROLE_CARD_STYLES[role].bg} ${ROLE_CARD_STYLES[role].border} ${ROLE_CARD_STYLES[role].text}`}
                  >
                    {ROLE_LABELS[role]}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={() =>
                open({
                  mode: "signup",
                  title: "Your stack is ready.",
                  description: "Where should we send it? Free account, no card.",
                  // Swaps the dialog's overview video for this result.
                  result: {
                    count: recommendations.length,
                    roleLabel: ROLE_LABELS[role],
                    intentLabel: intent.label.toLowerCase(),
                    chipClass: `${ROLE_CARD_STYLES[role].bg} ${ROLE_CARD_STYLES[role].border} ${ROLE_CARD_STYLES[role].text}`,
                  },
                })
              }
              className={`w-full sm:w-auto ${BUTTON.primary}`}
            >
              Show me my {recommendations.length} tools
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-muted-foreground mt-2">
              Free account. No card. Takes about twenty seconds.
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <button
            onClick={scrollToGrid}
            className="inline-flex items-center gap-1 min-h-11 font-medium text-foreground/70 hover:text-foreground transition-colors"
          >
            Browse all {ROLE_LABELS[role]} tools
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          {/* "Start over" is already on the Trail above this. Two of them, a
              few hundred pixels apart, doing the same thing. */}
        </div>
      </div>
    );
  }

  return null;
}

/** "Question 1 of 2". Tells people the quiz is short before they commit. */
function StepLabel({ current }: { current: 1 | 2 }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-3 mb-1">
      Question {current} of 2
    </p>
  );
}

/** The answer so far, read back, with the way out. */
function Trail({
  role,
  intent,
  onBack,
  label,
}: {
  role: Role;
  intent?: QuizIntent;
  onBack: () => void;
  label: string;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <p className="text-sm text-foreground/80">
        <span className={`font-semibold ${ROLE_CARD_STYLES[role].text}`}>
          {ROLE_ICONS[role]} {ROLE_LABELS[role]}
        </span>
        {intent ? (
          <span className="text-foreground/55">
            {" · "}
            {intent.label.toLowerCase()}
          </span>
        ) : null}
      </p>
      <button
        onClick={onBack}
        className="text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0 min-h-11 inline-flex items-center"
      >
        ← {label}
      </button>
    </div>
  );
}
